import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CheckIcon, XMarkIcon, ClockIcon } from '@heroicons/react/24/outline';
import { projectSharingService } from '../services/projectSharingService';
import { useProjects } from '../context/ProjectsContext';

function Section({ title, description, children }) {
  return (
    <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
      <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
        <h2 className="text-base font-semibold text-slate-900 dark:text-white">{title}</h2>
        {description && (
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{description}</p>
        )}
      </div>
      <div>{children}</div>
    </section>
  );
}

function InvitationRow({ invitation, children }) {
  return (
    <li className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4">
      <div className="min-w-0">
        <p className="text-sm text-slate-900 dark:text-white">
          <span className="font-semibold">{invitation.project_name}</span>
          <span className="text-slate-500 dark:text-slate-400"> ({invitation.project_jira_project_key})</span>
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          {invitation.inviter.display_name || invitation.inviter.email} → {invitation.invitee.display_name || invitation.invitee.email}
        </p>
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
          {new Date(invitation.created_at).toLocaleString()}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">{children}</div>
    </li>
  );
}

export default function InvitationsPage() {
  const { refreshIncomingCount, fetchProjects } = useProjects();
  const [incoming, setIncoming] = useState([]);
  const [pendingApproval, setPendingApproval] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        projectSharingService.getIncoming(),
        projectSharingService.getPendingApproval(),
        projectSharingService.getOutgoing(),
      ]);
      setIncoming(a || []);
      setPendingApproval(b || []);
      setOutgoing(c || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const run = async (id, fn, successMsg, refreshProjects = false) => {
    if (busy[id]) return;
    setBusy((m) => ({ ...m, [id]: true }));
    try {
      await fn();
      toast.success(successMsg);
      await loadAll();
      refreshIncomingCount();
      if (refreshProjects) await fetchProjects();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy((m) => ({ ...m, [id]: false }));
    }
  };

  const accept       = (inv) => run(inv.id, () => projectSharingService.accept(inv.id),       'Invitation accepted', true);
  const reject       = (inv) => run(inv.id, () => projectSharingService.reject(inv.id),       'Invitation rejected');
  const approve      = (inv) => run(inv.id, () => projectSharingService.approve(inv.id),      'Invitation approved');
  const ownerReject  = (inv) => run(inv.id, () => projectSharingService.ownerReject(inv.id),  'Invitation declined');
  const cancel       = (inv) => run(inv.id, () => projectSharingService.cancel(inv.id),       'Invitation cancelled');

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Invitations</h1>

      {loading ? (
        <p className="text-slate-500 dark:text-slate-400">Loading...</p>
      ) : (
        <>
          <Section
            title="For you"
            description="Invitations sent to you. Accepting replaces any local copy of the project."
          >
            {incoming.length === 0 ? (
              <p className="px-5 py-6 text-sm text-slate-500 dark:text-slate-400">Nothing pending.</p>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-700">
                {incoming.map((inv) => (
                  <InvitationRow key={inv.id} invitation={inv}>
                    <button
                      type="button"
                      disabled={busy[inv.id]}
                      onClick={() => accept(inv)}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold text-white"
                    >
                      <CheckIcon className="size-3.5" /> Accept
                    </button>
                    <button
                      type="button"
                      disabled={busy[inv.id]}
                      onClick={() => reject(inv)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200"
                    >
                      <XMarkIcon className="size-3.5" /> Reject
                    </button>
                  </InvitationRow>
                ))}
              </ul>
            )}
          </Section>

          <Section
            title="Awaiting your approval"
            description="Invitations created by members of projects you own."
          >
            {pendingApproval.length === 0 ? (
              <p className="px-5 py-6 text-sm text-slate-500 dark:text-slate-400">Nothing pending.</p>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-700">
                {pendingApproval.map((inv) => (
                  <InvitationRow key={inv.id} invitation={inv}>
                    <button
                      type="button"
                      disabled={busy[inv.id]}
                      onClick={() => approve(inv)}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold text-white"
                    >
                      <CheckIcon className="size-3.5" /> Approve
                    </button>
                    <button
                      type="button"
                      disabled={busy[inv.id]}
                      onClick={() => ownerReject(inv)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200"
                    >
                      <XMarkIcon className="size-3.5" /> Decline
                    </button>
                  </InvitationRow>
                ))}
              </ul>
            )}
          </Section>

          <Section
            title="Sent by you"
            description="Open invitations you've sent. Cancel any time before the recipient responds."
          >
            {outgoing.length === 0 ? (
              <p className="px-5 py-6 text-sm text-slate-500 dark:text-slate-400">No outgoing invitations.</p>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-700">
                {outgoing.map((inv) => (
                  <InvitationRow key={inv.id} invitation={inv}>
                    <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                      <ClockIcon className="size-3.5" />
                      {inv.status === 'pending_owner_approval' ? 'Awaiting owner' : 'Pending'}
                    </span>
                    <button
                      type="button"
                      disabled={busy[inv.id]}
                      onClick={() => cancel(inv)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200"
                    >
                      Cancel
                    </button>
                  </InvitationRow>
                ))}
              </ul>
            )}
          </Section>
        </>
      )}
    </div>
  );
}